import {
  type NodeData,
  type NodeId,
  type Point,
  snapToGrid,
} from '@ipfabric/utils';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import {
  type GraphNode,
  type GraphNodeDisplayObject,
  graphNodeFromNodeData,
  setGraphNodeSpriteIcon,
} from 'components/Graph/GraphApp/GraphNode';
import { ZINDEX_DROP_PREVIEW_NODE } from 'components/Graph/GraphApp/zIndexes';
import type { NodeMovePosition } from 'components/Graph/commands/setNodePositions';
import { readonlyArray } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { Sprite } from 'pixi.js';
import { ANCHOR_CENTER } from 'utils/pixi/constants';

const DROP_PREVIEW_NAME = 'dropPreview';

const drawPreview =
  (requestRender: () => Promise<void>) => (node: GraphNode) => {
    const dropPreviewNode = new Sprite();
    dropPreviewNode.name = DROP_PREVIEW_NAME;
    dropPreviewNode.anchor.set(ANCHOR_CENTER, ANCHOR_CENTER);
    setGraphNodeSpriteIcon(
      dropPreviewNode,
      node.data.type,
      requestRender,
      node.data.style,
    );
    dropPreviewNode.alpha = 0.4;

    dropPreviewNode.zIndex = ZINDEX_DROP_PREVIEW_NODE;
    node.displayObject.addChild(dropPreviewNode);
  };

export interface DropPreview {
  move: (positions: NodeMovePosition[]) => void;
  remove: () => void;
}

export const createDropPreview = (
  movingNodesData: readonly NodeData[],
  nodeDisplayObjects: Map<NodeId, GraphNodeDisplayObject>,
  requestRender: () => Promise<void>,
): DropPreview => {
  const movingNodesMap = pipe(
    movingNodesData,
    readonlyArray.filterMap(graphNodeFromNodeData(nodeDisplayObjects)),
    plainMap.fromArray((node) => node.data.id),
  );
  movingNodesMap.forEach(drawPreview(requestRender));

  const move = (positions: NodeMovePosition[]) => {
    for (const { nodeId, moveTo } of positions) {
      const dropPreviewNode = movingNodesMap.get(nodeId);

      if (
        dropPreviewNode !== undefined &&
        moveTo.x !== undefined &&
        moveTo.y !== undefined
      ) {
        const { x, y } = snapToGrid(moveTo as Point);
        const dropPreviewObject =
          dropPreviewNode.displayObject.getChildByName(DROP_PREVIEW_NAME);
        if (dropPreviewObject) {
          dropPreviewObject.position.x = x - dropPreviewNode.displayObject.x;
          dropPreviewObject.position.y = y - dropPreviewNode.displayObject.y;
        }
      }
    }
  };

  const remove = () => {
    // biome-ignore lint/complexity/noForEach: Iterating over Map
    movingNodesMap.forEach((node) =>
      node.displayObject.getChildByName(DROP_PREVIEW_NAME)?.destroy(),
    );
  };

  return {
    move,
    remove,
  };
};
